import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Shield, Plus, Edit2, Trash2, AlertTriangle, CheckCircle,
  DollarSign, Bell, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Guardrails = () => {
  const { shouldShowData } = useDemoData();

  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [guardrails, setGuardrails] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGuardrail, setEditingGuardrail] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    budget_limit: '',
    alert_threshold: 80,
    service: '',
    enabled: true
  });

  useEffect(() => {
    fetchGuardrails();
  }, []);

  const fetchGuardrails = async () => {
    try {
      const response = await axios.get(`${API_URL}/guardrails`);
      setGuardrails(response.data);
    } catch (error) {
      console.error('Failed to fetch guardrails:', error);
      toast.error('Failed to load guardrails');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        budget_limit: parseFloat(formData.budget_limit),
        alert_threshold: parseFloat(formData.alert_threshold),
        service: formData.service || null
      };

      if (editingGuardrail) {
        await axios.put(`${API_URL}/guardrails/${editingGuardrail.id}`, payload);
        toast.success('Guardrail updated successfully');
      } else {
        await axios.post(`${API_URL}/guardrails`, payload);
        toast.success('Guardrail created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchGuardrails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save guardrail');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this guardrail?')) return;
    
    try {
      await axios.delete(`${API_URL}/guardrails/${id}`);
      toast.success('Guardrail deleted');
      fetchGuardrails();
    } catch (error) {
      toast.error('Failed to delete guardrail');
    }
  };

  const openEditDialog = (guardrail) => {
    setEditingGuardrail(guardrail);
    setFormData({
      name: guardrail.name,
      budget_limit: guardrail.budget_limit.toString(),
      alert_threshold: guardrail.alert_threshold,
      service: guardrail.service || '',
      enabled: guardrail.enabled
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingGuardrail(null);
    setFormData({
      name: '',
      budget_limit: '',
      alert_threshold: 80,
      service: '',
      enabled: true
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'exceeded': return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800';
      case 'warning': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      default: return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800';
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'exceeded': return 'bg-rose-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-emerald-500';
    }
  };

  // Calculate totals
  const totalBudget = guardrails.reduce((sum, g) => sum + g.budget_limit, 0);
  const totalSpend = guardrails.reduce((sum, g) => sum + g.current_spend, 0);
  const exceededCount = guardrails.filter(g => g.status === 'exceeded').length;
  const warningCount = guardrails.filter(g => g.status === 'warning').length;

  if (!shouldShowData) {
    return <EmptyDataState title="Guardrails" description="Connect your cloud account to see real data here." />;
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="guardrails-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="p-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </Card>
          ))}
        </div>
        <Card className="p-6">
          <Skeleton className="h-64 w-full" />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="guardrails-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white"><BrandTitle>Budget Guardrails</BrandTitle></h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Set and monitor budget limits across your cloud infrastructure
          </p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-guardrail-btn"
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4" />
                Add Guardrail
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingGuardrail ? 'Edit Guardrail' : 'Create New Guardrail'}</DialogTitle>
                <DialogDescription>
                  Set a budget limit and alert threshold for monitoring.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Monthly AWS/Snowflake Budget"
                    required
                    data-testid="guardrail-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget_limit">Budget Limit ($)</Label>
                  <Input
                    id="budget_limit"
                    type="number"
                    value={formData.budget_limit}
                    onChange={(e) => setFormData({ ...formData, budget_limit: e.target.value })}
                    placeholder="50000"
                    required
                    data-testid="guardrail-budget-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="alert_threshold">Alert Threshold (%)</Label>
                  <Input
                    id="alert_threshold"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.alert_threshold}
                    onChange={(e) => setFormData({ ...formData, alert_threshold: e.target.value })}
                    placeholder="80"
                    required
                    data-testid="guardrail-threshold-input"
                  />
                  <p className="text-xs text-slate-400">Alert when spending reaches this percentage of budget</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service">Service (Optional)</Label>
                  <Input
                    id="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    placeholder="e.g., AWS EC2, Snowflake Warehouse (leave empty for all)"
                    data-testid="guardrail-service-input"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="enabled">Enabled</Label>
                  <Switch
                    id="enabled"
                    checked={formData.enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                    data-testid="guardrail-enabled-switch"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="guardrail-submit-btn">
                    {editingGuardrail ? 'Update' : 'Create'} Guardrail
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover" data-testid="stat-total-budget">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Total Budget</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{formatCurrency(totalBudget)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-total-spend">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-sky-900/30">
                <Shield className="w-5 h-5 text-emerald-600 dark:text-sky-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Current Spend</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{formatCurrency(totalSpend)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-exceeded">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30">
                <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Exceeded</p>
                <p className="text-xl font-bold text-rose-600 dark:text-rose-400">{exceededCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-warning">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Warnings</p>
                <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{warningCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Guardrails List */}
      <Card data-testid="guardrails-list">
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Manrope']">Active Guardrails</CardTitle>
          <CardDescription>Monitor budget utilization and alerts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {guardrails.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Shield className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No guardrails configured</p>
                <p className="text-sm mt-1">Create a guardrail to start monitoring your budgets</p>
              </div>
            ) : (
              guardrails.map((guardrail, idx) => {
                const utilizationPercent = (guardrail.current_spend / guardrail.budget_limit) * 100;
                
                return (
                  <div 
                    key={guardrail.id}
                    className={`p-5 rounded-xl border ${guardrail.enabled ? 'border-slate-200 dark:border-slate-700' : 'border-slate-100 dark:border-slate-800 opacity-60'} hover:shadow-md transition-all`}
                    data-testid={`guardrail-item-${idx}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-semibold text-slate-900 dark:text-white text-lg">
                            {guardrail.name}
                          </h3>
                          <Badge className={getStatusColor(guardrail.status)}>
                            {guardrail.status === 'ok' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {guardrail.status === 'warning' && <Bell className="w-3 h-3 mr-1" />}
                            {guardrail.status === 'exceeded' && <AlertTriangle className="w-3 h-3 mr-1" />}
                            {guardrail.status}
                          </Badge>
                          {!guardrail.enabled && (
                            <Badge variant="outline" className="text-slate-400">Disabled</Badge>
                          )}
                        </div>
                        
                        {guardrail.service && (
                          <p className="text-sm text-slate-400 mb-3">
                            Service: <span className="font-medium">{guardrail.service}</span>
                          </p>
                        )}
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">
                              {formatCurrency(guardrail.current_spend)} of {formatCurrency(guardrail.budget_limit)}
                            </span>
                            <span className={`font-medium ${utilizationPercent >= 100 ? 'text-rose-600' : utilizationPercent >= guardrail.alert_threshold ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {utilizationPercent.toFixed(1)}%
                            </span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${getProgressColor(guardrail.status)}`}
                              style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-slate-400">
                            Alert threshold: {guardrail.alert_threshold}%
                          </p>
                        </div>
                      </div>
                      
                      {isAdmin && (
                        <div className="flex gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(guardrail)}
                            data-testid={`edit-guardrail-${idx}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(guardrail.id)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                            data-testid={`delete-guardrail-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Guardrails;
